import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Logo from '../../components/common/Logo';

const PROVIDER_LOTTIE = 'https://lottie.host/b420c691-efad-45e4-bbb6-498c4851d8e7/WdLc33UyaK.lottie';
const CLIENT_LOTTIE   = 'https://lottie.host/5af1342f-b6eb-43d9-be4c-2f59a7e1f68b/D7x6OknXSj.lottie';

const roles = [
  {
    key: 'client',
    to: '/register/client',
    lottie: CLIENT_LOTTIE,
    badge: 'Free',
    badgeColor: '#22c55e',
    title: "I'm a Client",
    desc: 'Find and book verified beauty experts near you.',
    points: [
      'Browse providers by service & location',
      'Book appointments instantly',
      'Track your beauty history',
    ],
    cta: 'Join for free',
    accent: 'var(--gray-12)',
    border: 'var(--gray-4)',
    ctaBg: 'var(--gray-12)',
  },
  {
    key: 'provider',
    to: '/register/provider/plans',
    lottie: PROVIDER_LOTTIE,
    badge: 'From 20,000 UGX',
    badgeColor: 'var(--orange-9)',
    title: "I'm a Provider",
    desc: 'List your services and grow your client base.',
    points: [
      'Get discovered by clients near you',
      'Manage bookings & availability',
      'Accept payments seamlessly',
    ],
    cta: 'Start as a provider',
    accent: 'var(--orange-9)',
    border: 'var(--orange-5)',
    ctaBg: 'var(--orange-9)',
  },
];

const RolePicker = () => {
  const navigate = useNavigate();

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
        padding: '24px 16px 40px',
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Logo */}
      <div style={{ marginBottom: '32px' }}>
        <Logo size="large" dark />
      </div>

      {/* Heading */}
      <div style={{ textAlign: 'center', marginBottom: '36px' }}>
        <h1
          style={{
            fontSize: 'clamp(1.3rem, 4vw, 1.75rem)',
            fontWeight: 700,
            color: 'var(--gray-12)',
            margin: '0 0 8px',
            letterSpacing: '-0.02em',
          }}
        >
          How are you joining?
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: 0 }}>
          Choose your role — you can always create both types of accounts.
        </p>
      </div>

      {/* Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '16px',
          width: '100%',
          maxWidth: '600px',
        }}
      >
        {roles.map((role) => (
          <button
            key={role.key}
            type="button"
            onClick={() => navigate(role.to)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              background: '#fff',
              border: `1.5px solid ${role.border}`,
              borderRadius: '20px',
              padding: '28px 24px 24px',
              cursor: 'pointer',
              transition: 'box-shadow 0.18s, transform 0.18s, border-color 0.18s',
              fontFamily: "'Poppins', sans-serif",
              position: 'relative',
              outline: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 12px 36px -8px rgba(0,0,0,0.14)`;
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.borderColor = role.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = role.border;
            }}
          >
            {/* Badge */}
            <span
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                padding: '3px 8px',
                borderRadius: '999px',
                backgroundColor: role.key === 'client' ? '#dcfce7' : 'var(--orange-2)',
                color: role.badgeColor,
              }}
            >
              {role.badge}
            </span>

            {/* Lottie animation */}
            <div style={{ width: '140px', height: '140px', marginBottom: '12px' }}>
              <DotLottieReact src={role.lottie} loop autoplay style={{ width: '100%', height: '100%' }} />
            </div>

            {/* Title */}
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--gray-12)',
                margin: '0 0 6px',
                letterSpacing: '-0.01em',
              }}
            >
              {role.title}
            </h2>

            {/* Desc */}
            <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 16px', lineHeight: 1.5 }}>
              {role.desc}
            </p>

            {/* Feature points */}
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 24px',
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              {role.points.map((pt) => (
                <li
                  key={pt}
                  style={{
                    fontSize: '12px',
                    color: 'var(--gray-11)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '7px',
                  }}
                >
                  <span
                    style={{
                      marginTop: '2px',
                      flexShrink: 0,
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      backgroundColor: role.key === 'client' ? '#dcfce7' : 'var(--orange-2)',
                      color: role.key === 'client' ? '#16a34a' : 'var(--orange-9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </span>
                  {pt}
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div
              style={{
                width: '100%',
                padding: '11px 0',
                borderRadius: '10px',
                backgroundColor: role.ctaBg,
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.01em',
              }}
            >
              {role.cta}
            </div>
          </button>
        ))}
      </div>

      {/* Sign in link */}
      <p style={{ marginTop: '28px', fontSize: '13px', color: 'var(--gray-10)' }}>
        Already have an account?{' '}
        <a
          href="/login"
          style={{
            color: 'var(--orange-9)',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          Sign in
        </a>
      </p>
    </div>
  );
};

export default RolePicker;
